import React, { useEffect, useRef, useState } from "react";
import { api } from "../api";

// A capture is a background job: the POST that starts it returns straight
// away and the run continues on the server whether or not anyone is still
// looking at Compliance. This is the part that makes that visible — a pill
// in the corner of the whole app, so navigating away is obviously safe
// rather than something you hope is safe.
//
// It is also the only poller. The settings card polled for itself before,
// which meant a second timer whenever it happened to be on screen; now the
// timer lives here and the card listens for CAPTURE_UPDATED.

const KEY = "websiteCaptureRunning";
export const CAPTURE_STARTED = "website-capture:started";
export const CAPTURE_UPDATED = "website-capture:updated";

function flag(on: boolean) {
  try {
    if (on) window.sessionStorage.setItem(KEY, "1");
    else window.sessionStorage.removeItem(KEY);
  } catch { /* private mode — the pill just won't survive a reload */ }
}

function flagged(): boolean {
  try { return window.sessionStorage.getItem(KEY) === "1"; } catch { return false; }
}

/** Call after the run POST succeeds, or on landing mid-run, to raise the pill. */
export function markWebsiteCaptureStarted() {
  flag(true);
  window.dispatchEvent(new Event(CAPTURE_STARTED));
}

type Done = { captured: number; error?: string | null } | null;

export default function WebsiteCaptureIndicator() {
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState<Done>(null);
  const timer = useRef<number | null>(null);
  const hide = useRef<number | null>(null);

  useEffect(() => {
    const stop = () => {
      if (timer.current) { window.clearInterval(timer.current); timer.current = null; }
    };

    const tick = async () => {
      let d: any;
      try { d = await api.get("/firm-settings"); } catch { return; }
      window.dispatchEvent(new CustomEvent(CAPTURE_UPDATED, { detail: d }));
      const last = d?.website_capture_last || null;
      if (last?.running) return;
      stop();
      flag(false);
      setRunning(false);
      setDone({ captured: last?.captured?.length || 0, error: last?.error || null });
      if (hide.current) window.clearTimeout(hide.current);
      hide.current = window.setTimeout(() => setDone(null), 15000);
    };

    const start = () => {
      setDone(null);
      setRunning(true);
      stop();
      // Loose on purpose: a full site is minutes, and nobody is watching
      // this second by second.
      timer.current = window.setInterval(tick, 6000);
    };

    if (flagged()) start();
    window.addEventListener(CAPTURE_STARTED, start);
    return () => {
      stop();
      if (hide.current) window.clearTimeout(hide.current);
      window.removeEventListener(CAPTURE_STARTED, start);
    };
  }, []);

  if (!running && !done) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed", right: 18, bottom: 18, zIndex: 9000,
        display: "flex", alignItems: "center", gap: 10,
        background: "white", border: "1px solid #E5E7EB", borderRadius: 4,
        boxShadow: "0 8px 24px rgba(0,0,0,0.10)", padding: "10px 13px",
        maxWidth: 300,
      }}
    >
      <style>{"@keyframes wcap-spin{to{transform:rotate(360deg)}}"}</style>
      {running ? (
        <span
          aria-hidden
          style={{
            width: 14, height: 14, flexShrink: 0, borderRadius: "50%",
            border: "2px solid #E5E7EB", borderTopColor: "#111827",
            animation: "wcap-spin 0.8s linear infinite",
          }}
        />
      ) : (
        <span aria-hidden style={{ color: done?.error ? "#B91C1C" : "#059669", fontSize: 13 }}>
          {done?.error ? "×" : "●"}
        </span>
      )}
      <div style={{ fontSize: 12.5, color: "#111827", lineHeight: 1.5 }}>
        {running ? (
          <>
            Capturing your website…
            <span style={{ display: "block", fontSize: 11.5, color: "#6B7280" }}>
              Keep working, this runs in the background.
            </span>
          </>
        ) : done?.error ? (
          <>
            Website capture failed
            <span style={{ display: "block", fontSize: 11.5, color: "#6B7280" }}>
              {done.error}
            </span>
          </>
        ) : (
          <>
            Website capture finished
            <span style={{ display: "block", fontSize: 11.5, color: "#6B7280" }}>
              {done?.captured} {done?.captured === 1 ? "page" : "pages"} filed under
              {" "}Compliance → Firm Archive.
            </span>
          </>
        )}
      </div>
      {!running && (
        <button
          onClick={() => setDone(null)}
          aria-label="Dismiss"
          style={{ border: "none", background: "none", color: "#9CA3AF",
                   cursor: "pointer", fontSize: 15, lineHeight: 1, padding: 2 }}
        >
          ×
        </button>
      )}
    </div>
  );
}
